import * as React from 'react';

import {BulletedList} from 'shells/bulleted-list/bulleted-list';
import {Button} from 'shells/button/button';
import {Illustration} from 'shells/illustrations/illustration';
import {TwoPaneModal} from 'shells/modal/two-pane-modal';
import {Body} from 'shells/typography/body';
import {MarketingHeader} from 'shells/typography/marketing-header';

import type {User} from 'nutshell-graphql-types';

import {analyticsClient} from '../../../analytics-client';
import {useDismissibleUserSetting} from '../../../util/hooks/use-dismissible-user-setting';
import {initializeEmbeddedNsFormById} from '../../../util/initialize-embedded-ns-form-by-id';
import type {DefaultValues} from '../../../util/initialize-embedded-ns-form-by-id';

import {useGetSessionUser} from '../../core/use-get-session-user';
import {useOpenableModal, OpenableModals} from '../../openable-modals';

import styles from './adtech-fx-modal.module.css';

// From nutshell_4: https://app.nutshell.com/email-marketing/forms/3020-wfForms
const FORM_ID = 'IsXoVJ';

type Props = {
    // Depending on where this was opened from, we allow hiding it for a user
    canDismiss?: boolean;
};

export function AdTechFXModal(props: Props) {
    const [showAdTechForm, setShowAdTechForm] = React.useState<boolean>(false);
    const {onDismiss} = useDismissibleUserSetting('has_dismissed_adtech_fx_modal');

    const handleNotInterested = () => {
        if (onDismiss) {
            onDismiss();
        }

        onClose();
    };

    // Get user info to prefill form
    const {user} = useGetSessionUser();

    React.useEffect(() => {
        if (showAdTechForm) {
            initializeEmbeddedNsFormById(FORM_ID, getDefaultValues(user));
        }
    }, [showAdTechForm, user]);

    const {isOpen, onClose} = useOpenableModal(OpenableModals.ADTECH_FX_INTEREST_MODAL);

    React.useEffect(() => {
        if (isOpen) {
            analyticsClient.track(analyticsClient.EVENTS.TRACK_ADTECH_FX_MODAL_OPENED);
        }
    }, [isOpen]);

    return isOpen ? (
        <TwoPaneModal
            // @ts-expect-error
            isOpen={true}
            isWide={true}
            width={1000}
            minHeight={500}
            rightPaneWidth={600}
            onClose={onClose}
            leftSection={
                <div
                    className='flex align-center justify-center full-width full-height'
                    style={{backgroundColor: '#d6f2de'}}
                >
                    <Illustration src='person-working' />
                </div>
            }
            rightSection={
                showAdTechForm ? (
                    <div className='pt-64'>
                        <div id={`nutshell-form-${FORM_ID}`} className={styles['form-container']} />
                    </div>
                ) : (
                    <div
                        className='flex flex-dir-col justify-sb gap-16 py-64 border-box'
                        style={{maxWidth: 500, margin: '0 auto'}}
                    >
                        <div>
                            <MarketingHeader>
                                Use AdTech to target more leads with your Nutshell data
                            </MarketingHeader>
                            <Body size='large'>
                                AdTech uses technology from our colleagues at WebFX to fuse your
                                Nutshell data into surgical online advertising campaigns. You can
                                use AdTech to:
                            </Body>
                            <div className='mt-16'>
                                <BulletedList
                                    size='big'
                                    bullets={[
                                        <span key='bullet-1'>
                                            Find more leads that “look like” your best-qualified
                                            leads
                                        </span>,
                                        <span key='bullet-2'>
                                            Advertise to leads that are already in your pipeline
                                        </span>,
                                        <span key='bullet-3'>
                                            Advertise to companies that visit your website
                                        </span>,
                                    ]}
                                />
                            </div>
                            <Body size='large'>
                                AdTech uses your data to target display ads, video ads, even ads on
                                connected TV (like Hulu).
                            </Body>
                        </div>
                        <div className='flex justify-sb mt-16'>
                            {props.canDismiss ? (
                                <Button variant='text-secondary' onClick={handleNotInterested}>
                                    I’m not interested right now
                                </Button>
                            ) : (
                                <div />
                            )}
                            <Button
                                variant='primary'
                                size='xlarge'
                                onClick={() => setShowAdTechForm(true)}
                            >
                                Learn more
                            </Button>
                        </div>
                    </div>
                )
            }
        />
    ) : (
        <></>
    );
}

function getDefaultValues(user?: User | null): DefaultValues {
    const defaultValues: DefaultValues = {};

    if (user) {
        defaultValues.name = user.name;
        // @ts-expect-error
        defaultValues.email = user.emailAddresses.length > 0 ? user.emailAddresses[0].address : '';
    }

    return defaultValues;
}
